import type {
  LoadCapabilityRequirements,
  VehicleCapabilities,
} from "@loadsub/shared";
import {
  cacheAssignments,
  cachedAssignments,
  queueFile,
  queueJson,
} from "./offline";
export type CompanyRole = "owner" | "manager" | "driver";
export type LoadImportMapping = {
  id: string;
  companyId: string;
  name: string;
  fingerprint: string;
  headerRow: number;
  dataStartRow: number;
  columns: Record<string, number>;
  updatedAt: string;
};

export type CompanyAccess = {
  companyId: string;
  tradingName: string;
  status: "trial" | "active" | "grace" | "read_only" | "suspended" | "closed";
  roles: CompanyRole[];
  accountScope: "restricted" | "full";
  partner?: { code: string; name: string; serviceAccess: boolean };
};

export type Session = {
  sessionId: string;
  userId: string;
  email: string;
  emailVerified: boolean;
  platformRole: "owner" | "admin" | "support" | "finance" | null;
  canUseAs?: boolean;
  useAs?: {
    id: string;
    companyId: string;
    tradingName: string;
    expiresAt: string;
  } | null;
  companies: CompanyAccess[];
};

export type RegisterCompanyInput = {
  email: string;
  password: string;
  tradingName: string;
  legalName: string;
  businessType: "company" | "sole_trader" | "partnership" | "other";
  telephone: string;
  accountScope?: "restricted" | "full";
  attribution?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    landingPath?: string;
    referrerHost?: string;
  };
};

export type MemberUpdate = {
  manager: boolean;
  driver: boolean;
  active: boolean;
  remove?: boolean;
  displayName?: string;
  telephone?: string;
  licenceNumber?: string;
  licenceExpiry?: string;
  licenceCategories?: string[];
  cpcExpiry?: string;
  adrLevel?: "general" | "packages" | "tanks";
  adrClasses?: string[];
  adrExpiry?: string;
  notificationPreferences?: {
    assignments: boolean;
    messages: boolean;
    loadUpdates: boolean;
    compliance: boolean;
  };
};
export type CompanyMember = {
  userId: string;
  email: string;
  displayName?: string;
  telephone?: string;
  owner: boolean;
  manager: boolean;
  driver: boolean;
  active: boolean;
  removed: boolean;
  joinedAt: string;
  liveAssignments: Array<{
    loadId: string;
    collectionPostcode: string;
    deliveryPostcode: string;
    status: string;
  }>;
  compliance: {
    licenceNumber?: string;
    licenceExpiry?: string;
    licenceCategories: string[];
    cpcExpiry?: string;
    adrLevel?: "general" | "packages" | "tanks";
    adrClasses: string[];
    adrExpiry?: string;
  };
  notificationPreferences: {
    assignments: boolean;
    messages: boolean;
    loadUpdates: boolean;
    compliance: boolean;
  };
};
export type NavigationCounter = { total: number; pending: number };
export type NavigationSummary = {
  messages: NavigationCounter;
  assignedLoads: NavigationCounter;
  postedLoads: NavigationCounter;
  groups: NavigationCounter;
};
export type CompanyOnboardingGoal =
  "find_work" | "share_loads" | "manage_subcontractors" | "full_operation";
export type CompanyOnboardingEvent =
  | "presented"
  | "dismissed"
  | "cta_clicked"
  | "request_failed"
  | "dashboard_viewed";
export type CompanyOnboardingProgress = {
  goal: CompanyOnboardingGoal | null;
  facts: {
    profileComplete: boolean;
    vehicleAdded: boolean;
    driverAdded: boolean;
    groupConnected: boolean;
    addressAdded: boolean;
    bidPlaced: boolean;
    loadPosted: boolean;
    loadShared: boolean;
    bidReviewed: boolean;
    managedGroupCreated: boolean;
    managedGroupConfigured: boolean;
    subcontractorInvited: boolean;
    fixedOfferPosted: boolean;
    offerResponseReceived: boolean;
    notificationConfigured: boolean;
    insuranceAdded: boolean;
    driverComplianceAdded: boolean;
    workAssigned: boolean;
    jobCompleted: boolean;
    podAdded: boolean;
    invoiceCreated: boolean;
    teamMemberInvited: boolean;
    fiveSubcontractors: boolean;
  };
};
export type CheckoutSelection =
  | { kind: "full_account"; interval: "month" | "year"; voucherCode?: string }
  | { kind: "community_group"; groupId: string; voucherCode?: string }
  | {
      kind: "managed_network";
      groupId: string;
      interval: "month" | "year";
      voucherCode?: string;
    }
  | { kind: "tracking_connect"; voucherCode?: string };
export type BillingVoucherProduct = { id: string; name: string };
export type VoucherInput = {
  code: string;
  productId: string;
  stickerLabel: string;
  discountType: "percent" | "amount";
  discountValue: number;
  duration: "once" | "repeating" | "forever";
  durationMonths?: number | undefined;
  maxRedemptions?: number | undefined;
  expiresAt?: string | undefined;
  firstTimeOnly: boolean;
  public: boolean;
};
export type BillingVoucher = {
  id: string;
  code: string;
  name: string;
  productId: string | null;
  productName: string | null;
  active: boolean;
  public: boolean;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  expiresAt: string | null;
  firstTimeOnly: boolean;
  stickerLabel: string;
};
export type BillingSummary = {
  environment: "live" | "sandbox";
  customerConfigured: boolean;
  accessStatus: "trial" | "active" | "grace" | "read_only" | "exempt";
  graceDays: number;
  subscriptions: Array<{
    id: string;
    kind:
      | "full_account"
      | "community_group"
      | "managed_network"
      | "tracking_connect";
    groupId: string | null;
    groupName: string | null;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    managedSeatCapacity: number | null;
    graceEndsAt: string | null;
    lastInvoiceStatus: string | null;
  }>;
  invoices: Array<{
    id: string;
    groupId: string | null;
    groupName: string | null;
    status: string;
    currency: string;
    amountDue: number;
    amountPaid: number;
    hostedInvoiceUrl: string | null;
    invoicePdf: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    createdAt: string;
  }>;
  changeRequests: Array<{
    id: string;
    groupId: string;
    groupName: string;
    currentQuantity: number;
    requestedQuantity: number;
    monthlyDeltaPence: number;
    status: "pending" | "approved" | "rejected" | "failed";
    requestedByEmail: string;
    requestedAt: string;
  }>;
  billableGroups: Array<{
    id: string;
    name: string;
    scheme: "community" | "managed_subcontractor";
    managedSeatCapacity: number | null;
    qualifyingMemberCount: number;
    communityFeeWaivedAt: string | null;
  }>;
};

export type BankDetails = {
  accountName?: string;
  sortCode?: string;
  accountNumber?: string;
  iban?: string;
  bic?: string;
};
export type CompanyProfileInput = {
  tradingName: string;
  legalName: string;
  businessType: "company" | "sole_trader" | "partnership" | "other";
  registeredAddress: string;
  postcode: string;
  country: string;
  operationalContactName?: string;
  operationalEmail?: string;
  operationalTelephone: string;
  vatRegistered: boolean;
  vatNumber?: string;
  paymentTermsDays: number;
  bankDetails?: BankDetails;
};
export type CompanyProfile = CompanyProfileInput & {
  companyId: string;
  canEditSensitive: boolean;
  logo: { filename: string; contentType: string; updatedAt: string } | null;
  updatedAt: string;
};
export type AddressBookInput = {
  siteName: string;
  fullAddress: string;
  postcode: string;
  contactName?: string;
  contactTelephone?: string;
  contactEmail?: string;
  accessInstructions?: string;
  openingHours?: string;
  notes?: string;
};
export type AddressBookEntry = AddressBookInput & {
  id: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
};

export type CompanyInvitation = {
  id: string;
  email: string;
  manager: boolean;
  driver: boolean;
  expiresAt: string;
  createdAt: string;
  deliveryStatus: "pending" | "sent" | "failed";
  lastSentAt?: string;
  sendCount: number;
};
export type CompanyTeam = {
  members: CompanyMember[];
  invitations: CompanyInvitation[];
  billing: {
    activeUsers: number;
    includedUsers: number;
    additionalUsers: number;
  };
};
export type AdminOverview = {
  totals: {
    companies: number;
    users: number;
    groups: number;
    activeLoads: number;
  };
  companies: Array<{
    id: string;
    tradingName: string;
    status: CompanyAccess["status"];
    activeUsers: number;
    billingExempt: boolean;
    billingExemptReason: string | null;
    createdAt: string;
    trialEndsAt: string;
  }>;
  recentEvents: Array<{
    id: string;
    eventType: string;
    occurredAt: string;
    actorEmail: string | null;
  }>;
};
export type AdminPublicNetwork = {
  state: "open" | "closed";
  counts: Array<{
    environment: "live" | "sandbox" | "unassigned";
    tier: string;
    status: string;
    count: number;
  }>;
  members: Array<{
    companyId: string;
    companyName: string;
    tier: string;
    status: string;
    environment: string;
    registeredAt: string;
    activatedAt: string | null;
    rewardedAt: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    amountPaidPence: number;
  }>;
  recentEvents: AuditEvent[];
  stripeEvents: Array<{
    environment: string;
    eventId: string;
    eventType: string;
    status: "processed" | "failed" | "pending";
    attempts: number;
    createdAt: string;
    processedAt: string | null;
    lastError: string | null;
    companyName: string | null;
    customerId: string | null;
    subscriptionId: string | null;
    paymentIntentId: string | null;
    checkoutSessionId: string | null;
    amountPence: number | null;
    currency: string | null;
    livemode: boolean;
  }>;
};
export type AdminAcquisition = {
  totals: {
    registrations: number;
    groupCreators: number;
    paidCompanies: number;
  };
  bySource: Array<{
    source: string;
    registrations: number;
    groupCreators: number;
    paidCompanies: number;
  }>;
  byMedium: Array<{ medium: string; registrations: number }>;
  byCampaign: Array<{ campaign: string; registrations: number }>;
  byBusinessType: Array<{ businessType: string; registrations: number }>;
  trend: Array<{ date: string; registrations: number }>;
};
export type AuditFilters = {
  category?: string | undefined;
  eventType?: string | undefined;
  companyId?: string | undefined;
  userId?: string | undefined;
  entityType?: string | undefined;
  entityId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
};
export type AuditEvent = {
  id: string;
  eventType: string;
  category: string;
  actorUserId: string | null;
  actorEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  entityType: string;
  entityId: string | null;
  requestId: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
};
export type AuditResult = {
  events: AuditEvent[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
  options: {
    categories: string[];
    eventTypes: string[];
    entityTypes: string[];
    companies: Array<{ id: string; name: string }>;
    users: Array<{ id: string; email: string }>;
  };
};
export type AdminUser = {
  id: string;
  email: string;
  active: boolean;
  verified: boolean;
  platformRole: "owner" | "admin" | "support" | "finance" | null;
  companies: Array<{ id: string; name: string; roles: string[] }>;
  createdAt: string;
};
export type AdminInspectionKind =
  | "groups"
  | "subscriptions"
  | "loads"
  | "bids"
  | "assignments"
  | "pods"
  | "invoices"
  | "disputes"
  | "files";
export type AdminInspection = {
  kind: AdminInspectionKind;
  rows: Array<Record<string, unknown>>;
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};
export type InsuranceCatalogueItem = {
  insuranceType: string;
  label: string;
  description: string;
  defaultLevel: "not_required" | "advisory" | "mandatory";
  defaultMinimumCoverGbp: number | null;
  defaultEvidenceRequired: boolean;
  defaultWarningDays: number;
  defaultGraceDays: number;
  active: boolean;
  sortOrder: number;
};
export type AdminHealth = {
  api: { status: "ok"; checkedAt: string };
  worker: {
    status: "healthy" | "stale" | "never_run";
    lastRunAt: string | null;
  };
  jobs: Array<{
    id: string;
    workerName: string;
    jobName: string;
    status: "running" | "succeeded" | "failed";
    processedCount: number;
    errorMessage: string | null;
    startedAt: string;
    completedAt: string | null;
  }>;
};
export type AdminProspect = {
  id: string;
  companyName: string;
  rationale: string;
  websiteUrl: string | null;
  email: string | null;
  telephone: string | null;
  contactName: string | null;
  outreachSubject: string;
  outreachBody: string;
  contacted: boolean;
  contactedAt: string | null;
  createdAt: string;
  updatedAt: string;
};
export type AdminCampaign = {
  key: string;
  name: string;
  description: string;
  cooldownDays: number;
  active: boolean;
  audience: "all_verified" | "incomplete_onboarding";
  subject: string;
  heading: string;
  body: string;
  actionLabel: string;
  actionUrl: string;
  footer: string;
  feedbackEnabled: boolean;
  eligible: number;
  sent: number;
  feedback: number;
};
export type AdminCampaignInput = Pick<AdminCampaign, "name"|"description"|"cooldownDays"|"active"|"audience"|"subject"|"heading"|"body"|"actionLabel"|"actionUrl"|"footer">;
export type AdminCampaignCompany = {
  companyId: string;
  tradingName: string;
  userId: string;
  email: string;
  signupAt: string;
  lastLoginAt: string | null;
  goal: string | null;
  completedSteps: number;
  totalSteps: number;
  lastSentAt: string | null;
  eligible: boolean;
  exclusionReason: string | null;
};
export type AdminCampaignFeedback = {
  id: string;
  campaignKey: string;
  companyId: string;
  tradingName: string;
  userEmail: string;
  feedback: string;
  submittedAt: string;
  launchPackAwardedAt: string | null;
  launchPackClaimedAt: string | null;
};
export type GroupSummary = {
  id: string;
  name: string;
  description: string | null;
  rules: string;
  rulesVersion: number;
  brandColour: string;
  scheme: "community" | "managed_subcontractor";
  managedSeatCapacity: number | null;
  ownerCompanyId: string;
  ownerUserId: string;
  status: "active" | "closed" | "suspended";
};
export type GroupListItem = {
  group: GroupSummary;
  membershipStatus: "pending" | "active" | "rejected" | "removed" | null;
  isAdmin: boolean;
  isOwner: boolean;
  acceptedRulesVersion: number | null;
  rulesAcceptanceRequired: boolean;
  memberCount: number;
  pendingCount: number;
};
export type GroupMember = {
  companyId: string;
  tradingName: string;
  status: "pending" | "active" | "rejected" | "removed";
  biddingPaused: boolean;
  biddingPauseReason: string | null;
  sponsoredSlot: number | null;
  launchPackMembershipEndsAt: string | null;
  requestedAt: string;
  fleetTypes: string[];
  insurance: Array<{
    label: string;
    status: "current" | "expiring" | "expired" | "not_recorded";
    evidenceRecorded: boolean;
  }>;
};
export type GroupAdminCandidate = {
  userId: string;
  email: string;
  tradingName: string;
  admin: boolean;
  owner: boolean;
};
export type ManagedSeatSummary = {
  capacity: number;
  used: number;
  available: number;
  monthlyPricePence: number;
};
export type GroupManagementInsights = {
  actions: {
    pendingApplications: number;
    complianceExceptions: number;
    openDisputes: number;
  };
  headline: {
    totalMembers: number;
    activeMembers: number;
    loadsPosted: number;
    filledLoads: number;
    unfilledLoads: number;
    fillRatePercent: number;
    averageResponseMinutes: number | null;
    averageAssignmentMinutes: number | null;
  };
  vehicleDemand: Array<{
    vehicleClass: string;
    totalLoads: number;
    filledLoads: number;
    unfilledLoads: number;
  }>;
  busiestLanes: Array<{
    originArea: string;
    destinationArea: string;
    loads: number;
  }>;
};
export type GroupCommercialInsights = {
  assignedValuePence: number;
  averageWinningBidPence: number | null;
  averagePencePerMile: number | null;
  companyPerformance: Array<{
    companyId: string;
    tradingName: string;
    bids: number;
    assignments: number;
    averageResponseMinutes: number | null;
  }>;
};
export type GroupOpportunity = {
  kind: "vehicle_shortage" | "underserved_lane" | "demand_period";
  title: string;
  detail: string;
  sampleSize: number;
};
export type GroupOutstandingInvitation = {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
  sponsored: boolean;
  status:
    | "sent"
    | "opened"
    | "registered"
    | "verified"
    | "pending_approval"
    | "joined"
    | "active"
    | "expired";
  deliveredAt: string | null;
  openedAt: string | null;
  reminderSentAt: string | null;
  joinedAt: string | null;
  firstActionAt: string | null;
};
export type GroupDashboard = Omit<
  GroupListItem,
  "memberCount" | "pendingCount"
> & {
  members: GroupMember[];
  adminCandidates: GroupAdminCandidate[];
  managedSeats: ManagedSeatSummary | null;
  managementInsights: GroupManagementInsights | null;
  commercialInsights: GroupCommercialInsights | null;
  opportunities: GroupOpportunity[];
  sponsorships: {
    limit: number;
    used: number;
    reserved: number;
    available: number;
  } | null;
  launchPack: {
    awardId: string;
    used: number;
    capacity: number;
    available: number;
    benefitEndsAt: string;
  } | null;
  outstandingInvitations: GroupOutstandingInvitation[];
  newLoadNotifications: boolean;
};
export type VehicleStatus = "active" | "engaged" | "vor";
export type VehicleInput = {
  assetType: "powered" | "trailer";
  registration: string;
  vehicleClass: string;
  bodyType: string;
  make?: string | undefined;
  model?: string | undefined;
  payloadKg?: number | undefined;
  loadLengthMm?: number | undefined;
  loadWidthMm?: number | undefined;
  loadHeightMm?: number | undefined;
  overallHeightMm?: number | undefined;
  status: VehicleStatus;
  capabilities: VehicleCapabilities;
  couplingType?: "none" | "fifth_wheel" | "drawbar" | "both" | undefined;
  notes?: string | undefined;
};
export type Vehicle = VehicleInput & {
  id: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
};
export type InsuranceInput = {
  insuranceType:
    | "git"
    | "hire_and_reward"
    | "public_liability"
    | "employers_liability"
    | "other";
  insurer?: string | undefined;
  policyNumber?: string | undefined;
  coverValueGbp?: number | undefined;
  expiresOn?: string | undefined;
  notes?: string | undefined;
};
export type InsuranceEvidence = {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
};
export type InsurancePolicy = InsuranceInput & {
  id: string;
  companyId: string;
  evidence?: InsuranceEvidence | undefined;
  createdAt: string;
  updatedAt: string;
};
export type Fleet = {
  vehicles: Vehicle[];
  insurancePolicies: InsurancePolicy[];
  summary: { active: number; engaged: number; vor: number };
};

export type CredentialIntegrationKey =
  | "stripe"
  | "email"
  | "geocoding"
  | "web_push"
  | "maps"
  | "monitoring"
  | "meta_social"
  | "google_ads";
export type CredentialIntegrationInput = {
  provider: string;
  configuration: Record<string, string>;
  secrets?: Record<string, string>;
};
export type CredentialIntegrationSummary = {
  key: CredentialIntegrationKey;
  provider: string;
  configured: boolean;
  configuration: Record<string, string>;
  secretHints: Record<string, string>;
  updatedAt: string | null;
};
export type TrackingProviderKey =
  | "loadsub_device"
  | "webfleet"
  | "quartix"
  | "samsara"
  | "geotab"
  | "microlise";
export type TrackingProviderSummary = {
  key: TrackingProviderKey;
  displayName: string;
  enabled: boolean;
  authType: "platform_and_customer" | "customer_api_key" | "oauth";
  configured: boolean;
  configuration: Record<string, string>;
  secretHints: Record<string, string>;
  updatedAt: string | null;
};
export type CompanyTrackingConnection = {
  id: string;
  providerKey: TrackingProviderKey;
  providerName: string;
  status: "disconnected" | "configured" | "connected" | "error" | "paused";
  accountLabel: string | null;
  configuration: Record<string, string>;
  credentialHints: Record<string, string>;
  locationMatchingConsent: boolean;
  connectedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  updatedAt: string;
};
export type TrackingVehicleMapping = {
  id: string;
  vehicleId: string;
  registration: string;
  providerVehicleId: string;
  providerVehicleLabel: string | null;
  enabled: boolean;
};
export type TrackingDiscoveredVehicle = {
  id: string;
  label: string;
  registration?: string;
  mappedVehicleId?: string;
};
export type LoadTrackingPosition = {
  source: TrackingProviderKey;
  latitude: number;
  longitude: number;
  recordedAt: string;
  receivedAt: string;
  speedKph: number | null;
  headingDegrees: number | null;
  accuracyMetres: number | null;
  registration: string;
};
export type TrackingSummary = {
  entitled: boolean;
  usage: { trackedVehiclesThisMonth: number; monthlyPence: number };
  price: {
    tiers: Array<{ upToVehicles: number | null; monthlyPence: number }>;
  };
  providers: Array<
    Pick<
      TrackingProviderSummary,
      "key" | "displayName" | "enabled" | "authType" | "configured"
    > & { interestRegistered: boolean }
  >;
  connections: CompanyTrackingConnection[];
  mappings: TrackingVehicleMapping[];
};

export type StorageSettings = {
  configured: boolean;
  provider: "backblaze_b2";
  endpoint: string;
  region: string;
  bucket: string;
  keyIdHint: string | null;
  updatedAt: string | null;
};
export type StorageSettingsInput = {
  endpoint: string;
  region: string;
  bucket: string;
  keyId?: string | undefined;
  applicationKey?: string | undefined;
};
export type MarketingAsset = {
  id: string;
  title: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  objectKey: string;
  public: boolean;
  createdAt: string;
  updatedAt: string;
  publicUrl: string;
};
export type TimescaleKind =
  "asap" | "today" | "tomorrow" | "this_week" | "flexible" | "custom";
export type LoadReportFilters = {
  carrierCompanyId?: string;
  client?: string;
  destination?: string;
  groupId?: string;
  status?: string;
  from?: string;
  to?: string;
};
export type LoadReportRow = {
  loadId: string;
  publishedAt: string;
  reference: string;
  client: string;
  description: string;
  origin: string;
  destination: string;
  groups: string[];
  carrierCompanyId: string | null;
  carrier: string;
  status: string;
  acceptedValuePence: number;
  approvedExtrasPence: number;
  invoiceTotalPence: number;
  paymentStatus: string;
};
export type LoadReport = {
  rows: LoadReportRow[];
  totals: {
    loads: number;
    acceptedValuePence: number;
    approvedExtrasPence: number;
    invoiceTotalPence: number;
  };
  options: {
    carriers: Array<{ id: string; name: string }>;
    groups: Array<{ id: string; name: string }>;
  };
  filters: LoadReportFilters;
};
export type TrailerOperationAction =
  | "live_load"
  | "tip_unload"
  | "reload"
  | "drop_trailer"
  | "collect_trailer"
  | "swap_trailer"
  | "couple"
  | "uncouple"
  | "other";
export type TrailerLoadedState = "loaded" | "empty" | "unknown";
export type TrailerOperation = {
  operationOrder?: number | undefined;
  action: TrailerOperationAction;
  trailerIdentifier?: string | undefined;
  loadedState?: TrailerLoadedState | undefined;
  notes?: string | undefined;
  actualTrailerIdentifier?: string;
  actualLoadedState?: TrailerLoadedState | undefined;
  sealNumber?: string | undefined;
  conditionNotes?: string | undefined;
  completedAt?: string | undefined;
};
export type LoadStopInput = {
  action: "collect" | "deliver" | "both";
  goodsAction?: "load" | "unload" | "exchange" | "none";
  podRequired?: boolean;
  postcode: string;
  siteName?: string | undefined;
  address?: string | undefined;
  contactName?: string | undefined;
  contactTelephone?: string | undefined;
  contactEmail?: string | undefined;
  accessInstructions?: string | undefined;
  openingHours?: string | undefined;
  instructions?: string | undefined;
  operations?: TrailerOperation[] | undefined;
  lifecycleStatus?: "pending" | "arrived" | "completed" | "partial" | "failed";
  arrivedAt?: string;
  waitingStartedAt?: string;
  waitingEndedAt?: string;
  outcomeAt?: string;
  outcomeReason?: string;
  evidenceNotes?: string;
};
export type CreateLoadInput = {
  companyId: string;
  groupIds: string[];
  publicNetwork?: boolean;
  movementType: "goods" | "trailer";
  trailerCouplingType?: "semi" | "drawbar" | undefined;
  trailerLoadedState?: TrailerLoadedState | undefined;
  trailerBodyType?: string | undefined;
  description: string;
  publicNotes: string;
  timescaleKind: TimescaleKind;
  timescaleText: string;
  coLoadAllowed: boolean;
  isReturn: boolean;
  commercialMode: "open_bid" | "fixed_with_counter";
  fixedPaymentPence?: number | undefined;
  trailerNumber?: string | undefined;
  jobReference?: string | undefined;
  jobInstructions?: string | undefined;
  expiresAt?: string | undefined;
  posterMileage?: number | undefined;
  quantity?: number | undefined;
  weightKg?: number | undefined;
  lengthMm?: number | undefined;
  widthMm?: number | undefined;
  heightMm?: number | undefined;
  requiredVehicleClass?: string | undefined;
  requiredBodyType?: string | undefined;
  maximumVehicleClass?: string | undefined;
  accessRestrictions?: string | undefined;
  capabilityRequirements: LoadCapabilityRequirements;
  stops: LoadStopInput[];
};
export type BidInput = {
  companyId: string;
  vehicleId: string;
  amountPence: number;
  estimatedMileage: number;
  proposedCollectionEta?: string | undefined;
  proposedDeliveryEta?: string | undefined;
  conditions?: string | undefined;
  respondsToFixedOffer?: boolean | undefined;
  assumesCoLoad?: boolean | undefined;
  expiresAt?: string | undefined;
};
export type LoadBid = {
  id: string;
  loadId: string;
  bidderCompanyId: string;
  bidderTradingName: string;
  foundingEarlyAdopter?: boolean;
  vehicleId: string;
  vehicleRegistration: string;
  vehicleClass: string;
  bodyType: string;
  vehicleCapabilities: VehicleCapabilities;
  amountPence: number;
  estimatedMileage: number;
  pricePerMilePence: number;
  proposedCollectionEta?: string | undefined;
  proposedDeliveryEta?: string | undefined;
  conditions?: string | undefined;
  respondsToFixedOffer: boolean;
  assumesCoLoad?: boolean;
  expiresAt?: string | undefined;
  vatRegistered?: boolean;
  vatNumber?: string | undefined;
  reconfirmationRequired?: boolean;
  status: "pending" | "withdrawn" | "accepted" | "rejected";
  createdAt: string;
  updatedAt: string;
};
export type LoadMessage = {
  id: string;
  loadId: string;
  senderCompanyId: string;
  senderTradingName: string;
  recipientCompanyId: string;
  carrierCompanyId: string;
  carrierTradingName: string;
  body: string;
  createdAt: string;
};
export type LoadConversationSummary = {
  loadId: string;
  carrierCompanyId: string;
  routePostcodes: string[];
  description: string;
  loadStatus: "open" | "assigned";
  threadStatus: "enquiry" | "assigned" | "closed";
  otherCompanyId: string;
  otherCompanyName: string;
  poster: boolean;
  unread: boolean;
  canReply: boolean;
  lastMessage: {
    id: string;
    body: string;
    senderCompanyId: string;
    senderTradingName: string;
    createdAt: string;
  };
};
export type LoadSummary = Omit<
  CreateLoadInput,
  "companyId" | "groupIds" | "stops"
> & {
  id: string;
  publicReference: string;
  preview?: boolean;
  posterCompanyId: string;
  posterTradingName: string;
  status:
    | "open"
    | "assigned"
    | "in_transit"
    | "delivered"
    | "completed"
    | "cancelled"
    | "expired";
  stops: Array<LoadStopInput & { order: number }>;
  groups: Array<{ id: string; name: string }>;
  publishedAt: string;
  expiresAt?: string;
  closedAt?: string;
  closureReason?: string;
  postedByCurrentCompany: boolean;
  myBid?: LoadBid | undefined;
  bids?: LoadBid[] | undefined;
};
export type AssignmentWarning = { code: string; message: string };
export type AssignmentHistory = {
  id: string;
  driverUserId: string | null;
  driverEmail: string | null;
  poweredVehicleId: string;
  poweredVehicleRegistration: string;
  trailerId: string | null;
  trailerRegistration: string | null;
  changedByEmail: string;
  changeKind: "created" | "reassigned";
  changedAt: string;
};
export type LoadAssignment = {
  id: string;
  loadId: string;
  carrierCompanyId: string;
  posterCompanyName: string;
  description: string;
  timescaleText: string;
  routePostcodes: string[];
  loadStatus: "assigned" | "in_transit" | "delivered" | "completed";
  movementType: "goods" | "trailer";
  trailerCouplingType: "semi" | "drawbar" | null;
  driverUserId: string | null;
  driverEmail: string | null;
  poweredVehicleId: string;
  poweredVehicleRegistration: string;
  trailerId: string | null;
  trailerRegistration: string | null;
  currentExternalTrailerIdentifier: string | null;
  combinationMode: "solo" | "fleet_trailer" | "external_trailer";
  assignedAt: string;
  updatedAt: string;
  canManage: boolean;
  confidentialStops: Array<{
    order: number;
    postcode: string;
    siteName: string | null;
    address: string | null;
    contactName: string | null;
    contactTelephone: string | null;
    contactEmail: string | null;
    accessInstructions: string | null;
    openingHours: string | null;
    instructions: string | null;
    operations: TrailerOperation[];
    lifecycleStatus: "pending" | "arrived" | "completed" | "partial" | "failed";
    arrivedAt: string | null;
    waitingStartedAt: string | null;
    waitingEndedAt: string | null;
    outcomeAt: string | null;
    outcomeReason: string | null;
    evidenceNotes: string | null;
  }>;
  warnings: AssignmentWarning[];
  history: AssignmentHistory[];
  legs: Array<{
    fromStopOrder: number;
    toStopOrder: number;
    poweredVehicleRegistration: string;
    trailerRegistration: string | null;
    externalTrailerIdentifier: string | null;
    trailerLoadedState: TrailerLoadedState | null;
    combinationMode: "solo" | "fleet_trailer" | "external_trailer";
    recordedAt: string;
  }>;
};
export type AssignmentResource = {
  id: string;
  label: string;
  status?: string;
  assetType?: "powered" | "trailer";
  couplingType?: "none" | "fifth_wheel" | "drawbar" | "both";
};
export type AssignmentOptions = {
  drivers: AssignmentResource[];
  poweredVehicles: AssignmentResource[];
  trailers: AssignmentResource[];
};
export type LifecycleEvent = {
  id: string;
  eventType: string;
  stopOrder: number | null;
  fromStatus: string;
  toStatus: string;
  effectiveAt: string;
  reason: string | null;
  evidenceNotes: string | null;
  correctionOfEventId: string | null;
  createdAt: string;
  actorEmail: string;
};

export type PodEvidence = {
  id: string;
  kind: "paperwork" | "photo" | "generated_pdf";
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
};
export type PodHistory = {
  id: string;
  action: string;
  actorEmail: string;
  metadata: Record<string, unknown>;
  createdAt: string;
};
export type LoadPod = {
  id: string;
  loadId: string;
  carrierCompanyId: string;
  status:
    "draft" | "awaiting_review" | "changes_requested" | "approved" | "sent";
  recipientName: string;
  signatureData: string | null;
  notes: string | null;
  submittedByUserId: string;
  submittedAt: string;
  reviewedAt: string | null;
  sentAt: string | null;
  version: number;
  canReview: boolean;
  canSubmit: boolean;
  evidence: PodEvidence[];
  history: PodHistory[];
};

export type DisputeCategory =
  | "cancellation"
  | "no_show"
  | "delay"
  | "damage"
  | "shortage"
  | "payment"
  | "other";
export type ExitRequest = {
  id: string;
  loadId: string;
  kind: "poster_cancellation" | "carrier_withdrawal";
  requestedByCompanyId: string;
  requestedByCompanyName: string;
  reason: string;
  proposedChargePence: number | null;
  counterChargePence: number | null;
  status:
    "pending" | "accepted" | "rejected" | "escalated" | "closed" | "reopened";
  responseNote: string | null;
  createdAt: string;
  respondedAt: string | null;
  viewerIsRequester: boolean;
};
export type LoadDispute = {
  id: string;
  loadId: string;
  groupId: string;
  groupName: string;
  raisedByCompanyId: string;
  raisedByCompanyName: string;
  againstCompanyId: string;
  againstCompanyName: string;
  category: DisputeCategory;
  summary: string;
  details: string;
  status: "open" | "under_review" | "resolved" | "closed";
  outcome: string | null;
  formalAccess: boolean;
  canAdminister: boolean;
  evidence: Array<{
    id: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
    createdAt: string;
  }>;
  history: Array<{
    id: string;
    action: string;
    actorEmail: string;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
  createdAt: string;
  resolvedAt: string | null;
};
export type CaseWorkflow = {
  viewer: "poster" | "carrier" | "admin";
  loadStatus: string;
  route: string[];
  posterCompanyId: string;
  carrierCompanyId: string;
  exitRequests: ExitRequest[];
  disputes: LoadDispute[];
};

export type AdditionalCharge = {
  id: string;
  loadId: string;
  type: "waiting" | "demurrage" | "other";
  description: string;
  amountPence: number;
  status: "proposed" | "approved" | "disputed";
  disputeReason: string | null;
  proposedAt: string;
  reviewedAt: string | null;
};
export type LoadInvoice = {
  id: string;
  loadId: string;
  invoiceNumber: string;
  status: "draft" | "sent" | "paid" | "overdue" | "disputed";
  baseAmountPence: number;
  extrasAmountPence: number;
  vatRateBps: number;
  vatAmountPence: number;
  totalAmountPence: number;
  paymentTermsDays: number;
  dueOn: string | null;
  bankDetails: Record<string, string>;
  notes: string | null;
  sentAt: string | null;
  paidAt: string | null;
  disputedAt: string | null;
  disputeReason: string | null;
  viewer: "carrier" | "poster";
  carrierName: string;
  posterName: string;
  posterEmail: string;
  route: string[];
  items: Array<{
    id: string;
    type: "accepted_bid" | "additional_charge";
    sourceId: string | null;
    description: string;
    amountPence: number;
    sortOrder: number;
  }>;
  history: Array<{
    id: string;
    action: string;
    actorEmail: string;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
};
export type InvoiceWorkflow = {
  charges: AdditionalCharge[];
  invoice: LoadInvoice | null;
  viewer: "carrier" | "poster";
  loadStatus: string;
  acceptedBidPence: number;
};

export type UserNotification = {
  id: string;
  companyId: string;
  type: string;
  title: string;
  body: string;
  targetPath: string;
  entityType: string | null;
  entityId: string | null;
  actionable: boolean;
  readAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};
export type NotificationFeed = {
  notifications: UserNotification[];
  pendingActions: number;
  unread: number;
};

const apiBaseUrl = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export async function getSession(): Promise<Session | null> {
  try {
    const result = await request<{ session: Session }>("/api/v1/auth/session");
    return result.session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await request("/api/v1/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}
export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  await request("/api/v1/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}
export async function resendEmailVerification(email: string): Promise<void> {
  await request("/api/v1/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}
export async function verifyEmail(token: string): Promise<void> {
  await request("/api/v1/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export type LoginResult =
  | { authenticated: true; twoFactorRequired?: false }
  | { twoFactorRequired: true; challengeToken: string };
export async function login(
  email: string,
  password: string,
): Promise<LoginResult> {
  return request("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function verifyTwoFactor(
  challengeToken: string,
  code: string,
  rememberDevice: boolean,
): Promise<void> {
  await request("/api/v1/auth/two-factor/verify", {
    method: "POST",
    body: JSON.stringify({ challengeToken, code, rememberDevice }),
  });
}
export type SecuritySummary = {
  emailTwoFactorEnabled: boolean;
  trustedDevices: number;
  mandatory: boolean;
};
export async function getSecuritySummary(): Promise<SecuritySummary> {
  return request("/api/v1/auth/security");
}
export async function configureEmailTwoFactor(
  enabled: boolean,
): Promise<{ emailTwoFactorEnabled: boolean; recoveryCodes: string[] }> {
  return request("/api/v1/auth/security/email-two-factor", {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}
export async function revokeTrustedDevices(): Promise<void> {
  await request("/api/v1/auth/security/trusted-devices", { method: "DELETE" });
}

export async function registerCompany(
  input: RegisterCompanyInput,
): Promise<{ verificationRequired: boolean; emailSent: boolean }> {
  return request("/api/v1/auth/register-company", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function logout(): Promise<void> {
  await request("/api/v1/auth/logout", { method: "POST" });
}

export async function getNavigationSummary(
  companyId: string,
): Promise<NavigationSummary> {
  return (
    await request<{ summary: NavigationSummary }>(
      `/api/v1/companies/${companyId}/navigation-summary`,
    )
  ).summary;
}
export async function listAssignments(
  companyId: string,
): Promise<LoadAssignment[]> {
  try {
    const assignments = (
      await request<{ assignments: LoadAssignment[] }>(
        `/api/v1/assignments?companyId=${companyId}`,
      )
    ).assignments;
    await cacheAssignments(companyId, assignments);
    return assignments;
  } catch (error) {
    const cached = await cachedAssignments<LoadAssignment[]>(companyId);
    if (cached) return cached;
    throw error;
  }
}
export async function startDeviceTracking(loadId: string): Promise<void> {
  await request(`/api/v1/loads/${loadId}/device-tracking/start`, {
    method: "POST",
  });
}
export async function sendDevicePosition(
  loadId: string,
  input: {
    latitude: number;
    longitude: number;
    recordedAt: string;
    speedKph?: number;
    headingDegrees?: number;
    accuracyMetres?: number;
  },
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/device-tracking/position`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function stopDeviceTracking(loadId: string): Promise<void> {
  await request(`/api/v1/loads/${loadId}/device-tracking/stop`, {
    method: "POST",
  });
}
export async function getAssignmentOptions(
  companyId: string,
): Promise<AssignmentOptions> {
  return (
    await request<{ options: AssignmentOptions }>(
      `/api/v1/assignments/options?companyId=${companyId}`,
    )
  ).options;
}
export async function updateAssignment(
  loadId: string,
  input: {
    companyId: string;
    driverUserId: string;
    poweredVehicleId: string;
    trailerId?: string | null;
  },
): Promise<void> {
  await request(`/api/v1/assignments/${loadId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}
export async function applyLifecycle(
  loadId: string,
  input: {
    companyId: string;
    action: "start_journey" | "arrive" | "finish_stop" | "complete_load";
    stopOrder?: number;
    outcome?: "completed" | "partial" | "failed";
    reason?: string;
    evidenceNotes?: string;
    trailerOperations?: Array<{
      operationOrder: number;
      actualTrailerIdentifier?: string;
      actualLoadedState?: TrailerLoadedState;
      sealNumber?: string;
      conditionNotes?: string;
    }>;
  },
): Promise<void> {
  const path = `${apiBaseUrl}/api/v1/loads/${loadId}/lifecycle`;
  try {
    await request(`/api/v1/loads/${loadId}/lifecycle`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  } catch (error) {
    if (!offlineEligible(error)) throw error;
    await queueJson(path, "POST", input);
  }
}
export async function getLifecycleEvents(
  loadId: string,
  companyId: string,
): Promise<LifecycleEvent[]> {
  return (
    await request<{ events: LifecycleEvent[] }>(
      `/api/v1/loads/${loadId}/lifecycle?companyId=${companyId}`,
    )
  ).events;
}
export async function correctLifecycleTimestamp(
  loadId: string,
  input: {
    companyId: string;
    eventId: string;
    correctedAt: string;
    reason: string;
  },
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/lifecycle/corrections`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getCompanyOnboarding(
  companyId: string,
): Promise<CompanyOnboardingProgress> {
  return (
    await request<{ onboarding: CompanyOnboardingProgress }>(
      `/api/v1/companies/${companyId}/onboarding`,
    )
  ).onboarding;
}
export async function saveCompanyOnboardingGoal(
  companyId: string,
  goal: CompanyOnboardingGoal,
): Promise<CompanyOnboardingProgress> {
  return (
    await request<{ onboarding: CompanyOnboardingProgress }>(
      `/api/v1/companies/${companyId}/onboarding`,
      { method: "PUT", body: JSON.stringify({ goal }) },
    )
  ).onboarding;
}
export async function recordCompanyOnboardingEvent(
  companyId: string,
  event: CompanyOnboardingEvent,
  metadata: Record<string, string> = {},
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/onboarding/events`, {
    method: "POST",
    body: JSON.stringify({ event, metadata }),
  });
}
export async function getCompanyProfile(
  companyId: string,
): Promise<CompanyProfile> {
  return (
    await request<{ profile: CompanyProfile }>(
      `/api/v1/companies/${companyId}/profile`,
    )
  ).profile;
}
export async function saveCompanyProfile(
  companyId: string,
  input: CompanyProfileInput,
): Promise<CompanyProfile> {
  return (
    await request<{ profile: CompanyProfile }>(
      `/api/v1/companies/${companyId}/profile`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).profile;
}
export async function listAddressBook(
  companyId: string,
): Promise<AddressBookEntry[]> {
  return (
    await request<{ addresses: AddressBookEntry[] }>(
      `/api/v1/companies/${companyId}/address-book`,
    )
  ).addresses;
}
export async function createAddressBookEntry(
  companyId: string,
  input: AddressBookInput,
): Promise<AddressBookEntry> {
  return (
    await request<{ address: AddressBookEntry }>(
      `/api/v1/companies/${companyId}/address-book`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).address;
}
export async function updateAddressBookEntry(
  companyId: string,
  addressId: string,
  input: AddressBookInput,
): Promise<AddressBookEntry> {
  return (
    await request<{ address: AddressBookEntry }>(
      `/api/v1/companies/${companyId}/address-book/${addressId}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).address;
}
export async function archiveAddressBookEntry(
  companyId: string,
  addressId: string,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/address-book/${addressId}`, {
    method: "DELETE",
  });
}
export async function getReputation(
  companyId: string,
): Promise<ReputationSummary> {
  return (
    await request<{ reputation: ReputationSummary }>(
      `/api/v1/reputation/companies/${companyId}`,
    )
  ).reputation;
}
export async function getReviewOpportunities(
  companyId: string,
): Promise<ReviewOpportunity[]> {
  return (
    await request<{ opportunities: ReviewOpportunity[] }>(
      `/api/v1/reputation/companies/${companyId}/opportunities`,
    )
  ).opportunities;
}
export async function getReputationManagement(companyId: string) {
  return (
    await request<{
      management: {
        connections: Array<{ companyId: string; tradingName: string }>;
        pendingReferences: Array<{
          id: string;
          authorName: string;
          relationship: string;
          text: string;
          createdAt: string;
        }>;
      };
    }>(`/api/v1/reputation/companies/${companyId}/management`)
  ).management;
}
export async function createReview(input: {
  reviewerCompanyId: string;
  reviewedCompanyId: string;
  loadId: string;
  sentiment: "positive" | "neutral" | "negative";
  text: string;
}) {
  return (
    await request<{ review: { id: string } }>("/api/v1/reputation/reviews", {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).review;
}
export async function saveReviewReply(
  id: string,
  companyId: string,
  text: string,
): Promise<void> {
  await request(`/api/v1/reputation/reviews/${id}/reply`, {
    method: "PUT",
    body: JSON.stringify({ companyId, text }),
  });
}
export async function challengeReview(
  id: string,
  input: {
    companyId: string;
    explanation: string;
    requestedOutcome: "reply" | "amend" | "mute" | "remove";
  },
): Promise<void> {
  await request(`/api/v1/reputation/reviews/${id}/challenge`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function saveExternalReviewSource(
  companyId: string,
  provider: string,
  url: string,
) {
  return (
    await request<{ source: { id: string } }>(
      "/api/v1/reputation/external-source",
      { method: "PUT", body: JSON.stringify({ companyId, provider, url }) },
    )
  ).source;
}
export async function createCompanyReference(input: {
  authorCompanyId: string;
  subjectCompanyId: string;
  relationship: string;
  text: string;
}) {
  return request("/api/v1/reputation/references", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function decideCompanyReference(
  id: string,
  companyId: string,
  accept: boolean,
): Promise<void> {
  await request(`/api/v1/reputation/references/${id}/decision`, {
    method: "PUT",
    body: JSON.stringify({ companyId, accept }),
  });
}
export async function getReputationModeration() {
  return (
    await request<{
      moderation: {
        challenges: Array<{
          id: string;
          review_id: string;
          review_text: string;
          sentiment: string;
          reviewer_name: string;
          reviewed_name: string;
          explanation: string;
          requested_outcome: string;
          created_at: string;
        }>;
        reportedMedia: Array<{
          id: string;
          report_reason: string;
          original_filename: string;
          company_name: string;
          created_at: string;
        }>;
      };
    }>("/api/v1/admin/reputation/moderation")
  ).moderation;
}
export async function decideReviewChallenge(
  id: string,
  status: "upheld" | "muted" | "removed" | "closed",
  note: string,
) {
  await request(`/api/v1/admin/reputation/challenges/${id}`, {
    method: "PUT",
    body: JSON.stringify({ status, note }),
  });
}
export async function removeReportedReputationImage(id: string): Promise<void> {
  await request(`/api/v1/admin/reputation/images/${id}`, { method: "DELETE" });
}
export async function uploadReputationImage(
  kind: "review" | "reply" | "external",
  targetId: string,
  companyId: string,
  file: File,
) {
  const body = new FormData();
  body.append("file", file);
  return request<{ media: { id: string } }>(
    `/api/v1/reputation/images/${kind}/${targetId}?companyId=${companyId}`,
    { method: "POST", body },
  );
}
export async function reportReputationImage(
  id: string,
  companyId: string,
  reason: string,
): Promise<void> {
  await request(`/api/v1/reputation/images/${id}/report`, {
    method: "POST",
    body: JSON.stringify({ companyId, reason }),
  });
}

export async function uploadCompanyLogo(
  companyId: string,
  file: File,
): Promise<void> {
  const body = new FormData();
  body.append("file", file);
  await request(`/api/v1/companies/${companyId}/logo`, {
    method: "POST",
    body,
  });
}
export async function getCompanyLogoUrl(companyId: string): Promise<string> {
  await request(`/api/v1/companies/${companyId}/logo`);
  return `${apiBaseUrl}/api/v1/companies/${companyId}/logo/content?v=${Date.now()}`;
}
export async function removeCompanyLogo(companyId: string): Promise<void> {
  await request(`/api/v1/companies/${companyId}/logo`, { method: "DELETE" });
}
export async function uploadGroupLogo(
  groupId: string,
  companyId: string,
  file: File,
): Promise<void> {
  const body = new FormData();
  body.append("file", file);
  await request(`/api/v1/groups/${groupId}/logo?companyId=${companyId}`, {
    method: "POST",
    body,
  });
}
export async function getGroupLogoUrl(
  groupId: string,
  companyId: string,
): Promise<string> {
  await request(`/api/v1/groups/${groupId}/logo?companyId=${companyId}`);
  return `${apiBaseUrl}/api/v1/groups/${groupId}/logo/content?companyId=${encodeURIComponent(companyId)}&v=${Date.now()}`;
}
export async function removeGroupLogo(
  groupId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/logo?companyId=${companyId}`, {
    method: "DELETE",
  });
}

export async function getCompanyTeam(companyId: string): Promise<CompanyTeam> {
  return (
    await request<{ team: CompanyTeam }>(`/api/v1/companies/${companyId}/team`)
  ).team;
}

export async function inviteCompanyUser(
  companyId: string,
  input: { email: string; manager: boolean; driver: boolean },
) {
  return (
    await request<{ invitation: CompanyInvitation & { token: string } }>(
      `/api/v1/companies/${companyId}/invitations`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).invitation;
}

export async function updateCompanyMember(
  companyId: string,
  userId: string,
  input: MemberUpdate,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/members/${userId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function resendCompanyInvitation(
  companyId: string,
  invitationId: string,
) {
  return (
    await request<{ invitation: CompanyInvitation & { token: string } }>(
      `/api/v1/companies/${companyId}/invitations/${invitationId}/resend`,
      { method: "POST" },
    )
  ).invitation;
}

export async function revokeCompanyInvitation(
  companyId: string,
  invitationId: string,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/invitations/${invitationId}`, {
    method: "DELETE",
  });
}

export async function acceptCompanyInvitation(
  token: string,
  password: string,
): Promise<void> {
  await request("/api/v1/company-invitations/accept", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return (await request<{ overview: AdminOverview }>("/api/v1/admin/overview"))
    .overview;
}
export async function getAdminAcquisition(): Promise<AdminAcquisition> {
  return (
    await request<{ acquisition: AdminAcquisition }>(
      "/api/v1/admin/acquisition",
    )
  ).acquisition;
}
export async function getAdminAudit(
  filters: AuditFilters = {},
): Promise<AuditResult> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters))
    if (value !== undefined && value !== "") query.set(key, String(value));
  return (await request<{ audit: AuditResult }>(`/api/v1/admin/audit?${query}`))
    .audit;
}
export async function getAdminUsers(): Promise<AdminUser[]> {
  return (await request<{ users: AdminUser[] }>("/api/v1/admin/users")).users;
}
export async function updateAdminUser(
  id: string,
  input: {
    active?: boolean;
    platformRole?: "admin" | "support" | "finance" | null;
  },
) {
  await request(`/api/v1/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
export async function updateAdminCompany(
  id: string,
  input: {
    status?: CompanyAccess["status"];
    billingExempt?: boolean;
    billingExemptReason?: string | null | undefined;
  },
) {
  await request(`/api/v1/admin/companies/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
export async function startUseAs(companyId: string): Promise<void> {
  await request("/api/v1/admin/use-as", {
    method: "POST",
    body: JSON.stringify({ companyId }),
  });
}
export async function endUseAs(): Promise<void> {
  await request("/api/v1/admin/use-as/end", { method: "POST" });
}
export async function getAdminInspection(
  kind: AdminInspectionKind,
  page = 1,
): Promise<AdminInspection> {
  return (
    await request<{ inspection: AdminInspection }>(
      `/api/v1/admin/inspect/${kind}?page=${page}`,
    )
  ).inspection;
}
export async function getInsuranceCatalogue(): Promise<
  InsuranceCatalogueItem[]
> {
  return (
    await request<{ items: InsuranceCatalogueItem[] }>(
      "/api/v1/admin/insurance-catalogue",
    )
  ).items;
}
export async function saveInsuranceCatalogue(items: InsuranceCatalogueItem[]) {
  await request("/api/v1/admin/insurance-catalogue", {
    method: "PUT",
    body: JSON.stringify({ items }),
  });
}
export async function getAdminSettings(): Promise<Record<string, unknown>> {
  return (
    await request<{ settings: Record<string, unknown> }>(
      "/api/v1/admin/settings",
    )
  ).settings;
}
export async function saveAdminSetting(key: string, value: unknown) {
  await request(`/api/v1/admin/settings/${key}`, {
    method: "PUT",
    body: JSON.stringify({ value }),
  });
}
export async function getAdminPublicNetwork(): Promise<AdminPublicNetwork> {
  return (
    await request<{ publicNetwork: AdminPublicNetwork }>(
      "/api/v1/admin/public-network",
    )
  ).publicNetwork;
}
export async function setAdminPublicNetworkState(
  state: "open" | "closed",
): Promise<void> {
  await request("/api/v1/admin/public-network/state", {
    method: "PUT",
    body: JSON.stringify({ state }),
  });
}
export async function getAdminHealth(): Promise<AdminHealth> {
  return (await request<{ health: AdminHealth }>("/api/v1/admin/health"))
    .health;
}
export async function queueFileMigration(
  targetProvider: string,
  target: StorageSettingsInput,
): Promise<{ queued: number; verified: number }> {
  return request<{ queued: number; verified: number }>(
    "/api/v1/admin/file-migrations",
    { method: "POST", body: JSON.stringify({ targetProvider, target }) },
  );
}

export async function listGroups(companyId: string): Promise<GroupListItem[]> {
  return (
    await request<{ groups: GroupListItem[] }>(
      `/api/v1/groups?companyId=${companyId}`,
    )
  ).groups;
}
export async function getGroup(
  groupId: string,
  companyId: string,
): Promise<GroupDashboard> {
  return (
    await request<{ dashboard: GroupDashboard }>(
      `/api/v1/groups/${groupId}?companyId=${companyId}`,
    )
  ).dashboard;
}
export async function updateGroup(
  groupId: string,
  input: {
    companyId: string;
    name: string;
    description?: string;
    rules?: string;
    brandColour?: string;
  },
): Promise<GroupSummary> {
  return (
    await request<{ group: GroupSummary }>(`/api/v1/groups/${groupId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    })
  ).group;
}
export async function clearGroupSponsorship(
  groupId: string,
  slot: number,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/sponsorships/${slot}`, {
    method: "DELETE",
  });
}
export async function createGroup(input: {
  companyId: string;
  name: string;
  description?: string;
  rules?: string;
  useTrial: boolean;
  scheme: "community" | "managed_subcontractor";
}): Promise<GroupSummary> {
  return (
    await request<{ group: GroupSummary }>("/api/v1/groups", {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).group;
}
export async function createGroupInvitation(
  groupId: string,
  email: string,
  sponsored = false,
  reusable = false,
): Promise<{
  token: string;
  deliveryStatus: "sent" | "failed" | "not_requested";
  sponsorshipSlot: number | null;
}> {
  const expiresAt = reusable
    ? undefined
    : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
  return (
    await request<{
      invitation: {
        token: string;
        deliveryStatus: "sent" | "failed" | "not_requested";
        sponsorshipSlot: number | null;
      };
    }>(`/api/v1/groups/${groupId}/invitations`, {
      method: "POST",
      body: JSON.stringify({
        ...(expiresAt ? { expiresAt } : {}),
        ...(email ? { email } : {}),
        sponsored,
        reusable,
      }),
    })
  ).invitation;
}
export async function resendGroupInvitation(
  groupId: string,
  invitationId: string,
): Promise<{
  token: string;
  deliveryStatus: "sent" | "failed" | "not_requested";
  sponsorshipSlot: number | null;
}> {
  return (
    await request<{
      invitation: {
        token: string;
        deliveryStatus: "sent" | "failed" | "not_requested";
        sponsorshipSlot: number | null;
      };
    }>(`/api/v1/groups/${groupId}/invitations/${invitationId}/resend`, {
      method: "POST",
    })
  ).invitation;
}
export type GroupInvitationPreview = Pick<
  GroupSummary,
  | "id"
  | "name"
  | "description"
  | "rules"
  | "rulesVersion"
  | "brandColour"
  | "scheme"
> & {
  sponsored: boolean;
  insuranceRules: Array<{
    insuranceType: string;
    label: string;
    level: "advisory" | "mandatory";
    minimumCoverGbp: number | null;
    evidenceRequired: boolean;
    warningDays: number;
    graceDays: number;
  }>;
};
export async function previewGroupInvitation(
  groupId: string,
  invitationToken: string,
): Promise<GroupInvitationPreview> {
  return (
    await request<{ group: GroupInvitationPreview }>(
      `/api/v1/groups/${groupId}/invitations/${encodeURIComponent(invitationToken)}/preview`,
    )
  ).group;
}
export async function applyToGroup(
  groupId: string,
  companyId: string,
  invitationToken: string,
  acceptedRulesVersion: number,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/applications`, {
    method: "POST",
    body: JSON.stringify({ companyId, invitationToken, acceptedRulesVersion }),
  });
}
export async function acknowledgeGroupRules(
  groupId: string,
  companyId: string,
  rulesVersion: number,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/rules/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ companyId, rulesVersion }),
  });
}
export async function reviewGroupApplication(
  groupId: string,
  companyId: string,
  approved: boolean,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/applications/${companyId}/review`, {
    method: "POST",
    body: JSON.stringify({ approved }),
  });
}
export async function setGroupBiddingPause(
  groupId: string,
  companyId: string,
  paused: boolean,
  reason?: string,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/members/${companyId}/bidding`, {
    method: "PUT",
    body: JSON.stringify({ paused, ...(reason ? { reason } : {}) }),
  });
}
export async function setGroupAdmin(
  groupId: string,
  userId: string,
  active: boolean,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/admins/${userId}`, {
    method: "PUT",
    body: JSON.stringify({ active }),
  });
}
export async function setGroupNewLoadNotifications(
  groupId: string,
  companyId: string,
  enabled: boolean,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/notifications/new-loads`, {
    method: "PUT",
    body: JSON.stringify({ companyId, enabled }),
  });
}
export async function closeGroup(
  groupId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}`, {
    method: "DELETE",
    body: JSON.stringify({ companyId }),
  });
}
export async function assignGroupSponsorship(
  groupId: string,
  slot: number,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/sponsorships/${slot}`, {
    method: "PUT",
    body: JSON.stringify({ companyId }),
  });
}
export async function upgradeCompanyAccount(companyId: string): Promise<void> {
  await request(`/api/v1/companies/${companyId}/account/upgrade`, {
    method: "POST",
  });
}
export async function getFleet(companyId: string): Promise<Fleet> {
  return (
    await request<{ fleet: Fleet }>(`/api/v1/companies/${companyId}/fleet`)
  ).fleet;
}
export async function createVehicle(
  companyId: string,
  input: VehicleInput,
): Promise<Vehicle> {
  return (
    await request<{ vehicle: Vehicle }>(
      `/api/v1/companies/${companyId}/vehicles`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).vehicle;
}
export async function updateVehicle(
  companyId: string,
  vehicleId: string,
  input: VehicleInput,
): Promise<Vehicle> {
  return (
    await request<{ vehicle: Vehicle }>(
      `/api/v1/companies/${companyId}/vehicles/${vehicleId}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).vehicle;
}
export async function archiveVehicle(
  companyId: string,
  vehicleId: string,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/vehicles/${vehicleId}`, {
    method: "DELETE",
  });
}
export async function createInsurance(
  companyId: string,
  input: InsuranceInput,
): Promise<InsurancePolicy> {
  return (
    await request<{ policy: InsurancePolicy }>(
      `/api/v1/companies/${companyId}/insurance`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).policy;
}
export async function updateInsurance(
  companyId: string,
  policyId: string,
  input: InsuranceInput,
): Promise<InsurancePolicy> {
  return (
    await request<{ policy: InsurancePolicy }>(
      `/api/v1/companies/${companyId}/insurance/${policyId}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).policy;
}
export async function archiveInsurance(
  companyId: string,
  policyId: string,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/insurance/${policyId}`, {
    method: "DELETE",
  });
}

export async function getCredentialIntegrations(): Promise<
  CredentialIntegrationSummary[]
> {
  return (
    await request<{ integrations: CredentialIntegrationSummary[] }>(
      "/api/v1/admin/integrations",
    )
  ).integrations;
}
export async function saveCredentialIntegration(
  key: CredentialIntegrationKey,
  input: CredentialIntegrationInput,
): Promise<CredentialIntegrationSummary> {
  return (
    await request<{ integration: CredentialIntegrationSummary }>(
      `/api/v1/admin/integrations/${key}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).integration;
}
export async function getStorageSettings(): Promise<StorageSettings> {
  return (
    await request<{ settings: StorageSettings }>(
      "/api/v1/admin/integrations/storage",
    )
  ).settings;
}
export async function saveStorageSettings(
  input: StorageSettingsInput,
): Promise<StorageSettings> {
  return (
    await request<{ settings: StorageSettings }>(
      "/api/v1/admin/integrations/storage",
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).settings;
}
export async function testStorageSettings(
  input?: StorageSettingsInput,
): Promise<void> {
  await request("/api/v1/admin/integrations/storage/test", {
    method: "POST",
    ...(input ? { body: JSON.stringify(input) } : {}),
  });
}
export async function getTrackingProviders(): Promise<
  TrackingProviderSummary[]
> {
  return (
    await request<{ providers: TrackingProviderSummary[] }>(
      "/api/v1/admin/integrations/fleet-tracking",
    )
  ).providers;
}
export async function saveTrackingProvider(
  key: TrackingProviderKey,
  input: {
    enabled: boolean;
    authType: TrackingProviderSummary["authType"];
    configuration: Record<string, string>;
    secrets?: Record<string, string>;
  },
): Promise<TrackingProviderSummary> {
  return (
    await request<{ provider: TrackingProviderSummary }>(
      `/api/v1/admin/integrations/fleet-tracking/${key}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).provider;
}
export async function getCompanyTracking(
  companyId: string,
): Promise<TrackingSummary> {
  return request(`/api/v1/companies/${companyId}/integrations/fleet-tracking`);
}
export async function registerTrackingProviderInterest(
  companyId: string,
  key: TrackingProviderKey,
): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/integrations/fleet-tracking/${key}/interest`,
    { method: "POST" },
  );
}
export async function saveCompanyTracking(
  companyId: string,
  key: TrackingProviderKey,
  input: {
    accountLabel?: string;
    configuration: Record<string, string>;
    credentials?: Record<string, string>;
    locationMatchingConsent: boolean;
  },
): Promise<CompanyTrackingConnection> {
  return (
    await request<{ connection: CompanyTrackingConnection }>(
      `/api/v1/companies/${companyId}/integrations/fleet-tracking/${key}`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).connection;
}
export async function disconnectCompanyTracking(
  companyId: string,
  key: TrackingProviderKey,
): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/integrations/fleet-tracking/${key}`,
    { method: "DELETE" },
  );
}
export async function saveTrackingVehicleMapping(
  companyId: string,
  key: TrackingProviderKey,
  input: {
    vehicleId: string;
    providerVehicleId: string;
    providerVehicleLabel?: string;
    enabled: boolean;
  },
): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/integrations/fleet-tracking/${key}/vehicles`,
    { method: "PUT", body: JSON.stringify(input) },
  );
}
export async function discoverTrackingVehicles(
  companyId: string,
  key: TrackingProviderKey,
): Promise<TrackingDiscoveredVehicle[]> {
  return (
    await request<{ vehicles: TrackingDiscoveredVehicle[] }>(
      `/api/v1/companies/${companyId}/integrations/fleet-tracking/${key}/vehicles`,
    )
  ).vehicles;
}
export async function getLoadTrackingPosition(
  companyId: string,
  loadId: string,
): Promise<LoadTrackingPosition | null> {
  return (
    await request<{ position: LoadTrackingPosition | null }>(
      `/api/v1/loads/${loadId}/tracking?companyId=${companyId}`,
    )
  ).position;
}
export async function getLoadTrackingSharing(
  companyId: string,
  loadId: string,
): Promise<{ sharedWithPoster: boolean }> {
  return request(
    `/api/v1/loads/${loadId}/tracking-sharing?companyId=${companyId}`,
  );
}
export async function setLoadTrackingSharing(
  companyId: string,
  loadId: string,
  sharedWithPoster: boolean,
): Promise<{ sharedWithPoster: boolean }> {
  return request(`/api/v1/loads/${loadId}/tracking-sharing`, {
    method: "PUT",
    body: JSON.stringify({ companyId, sharedWithPoster }),
  });
}
export async function getMarketingAssets(): Promise<MarketingAsset[]> {
  return (
    await request<{ assets: MarketingAsset[] }>(
      "/api/v1/admin/marketing-assets",
    )
  ).assets;
}
export async function uploadMarketingAsset(
  title: string,
  file: File,
): Promise<MarketingAsset> {
  const prepared = await request<{
    id: string;
    objectKey: string;
    uploadUrl: string;
  }>("/api/v1/admin/marketing-assets/upload", {
    method: "POST",
    body: JSON.stringify({
      title,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    }),
  });
  const uploaded = await fetch(prepared.uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });
  if (!uploaded.ok)
    throw new ApiError(
      "B2 rejected the media upload. Check the bucket CORS permissions.",
      uploaded.status,
    );
  return (
    await request<{ asset: MarketingAsset }>(
      "/api/v1/admin/marketing-assets/complete",
      {
        method: "POST",
        body: JSON.stringify({
          ...prepared,
          title,
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        }),
      },
    )
  ).asset;
}
export async function updateMarketingAsset(
  id: string,
  input: { title?: string; public?: boolean },
): Promise<MarketingAsset> {
  return (
    await request<{ asset: MarketingAsset }>(
      `/api/v1/admin/marketing-assets/${id}`,
      { method: "PATCH", body: JSON.stringify(input) },
    )
  ).asset;
}
export async function deleteMarketingAsset(id: string): Promise<void> {
  await request(`/api/v1/admin/marketing-assets/${id}`, { method: "DELETE" });
}
export async function downloadMarketingAsset(id: string): Promise<void> {
  const value = await request<{ url: string }>(
    `/api/v1/admin/marketing-assets/${id}/download`,
  );
  window.location.assign(value.url);
}
export function marketingAssetPublicUrl(asset: MarketingAsset) {
  return `${apiBaseUrl}${asset.publicUrl}`;
}
export async function uploadInsuranceEvidence(
  companyId: string,
  policyId: string,
  file: File,
): Promise<InsuranceEvidence> {
  const body = new FormData();
  body.append("file", file);
  return (
    await request<{ evidence: InsuranceEvidence }>(
      `/api/v1/companies/${companyId}/insurance/${policyId}/evidence`,
      { method: "POST", body },
    )
  ).evidence;
}
export async function downloadInsuranceEvidence(
  companyId: string,
  policyId: string,
): Promise<void> {
  const result = await request<{ url: string }>(
    `/api/v1/companies/${companyId}/insurance/${policyId}/evidence/download`,
  );
  window.location.assign(result.url);
}
export async function removeInsuranceEvidence(
  companyId: string,
  policyId: string,
): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/insurance/${policyId}/evidence`,
    { method: "DELETE" },
  );
}
export async function listLoads(
  companyId: string,
  mode: "available" | "posted" | "bids" | "assigned" = "available",
): Promise<LoadSummary[]> {
  return (
    await request<{ loads: LoadSummary[] }>(
      `/api/v1/loads?companyId=${companyId}&mode=${mode}`,
    )
  ).loads;
}
export async function createLoad(
  input: CreateLoadInput,
  idempotencyKey?: string,
): Promise<string> {
  return (
    await request<{ id: string }>("/api/v1/loads", {
      method: "POST",
      body: JSON.stringify(input),
      ...(idempotencyKey
        ? { headers: { "x-idempotency-key": idempotencyKey } }
        : {}),
    })
  ).id;
}
export async function getLoadImportMapping(
  companyId: string,
  fingerprint: string,
): Promise<LoadImportMapping | null> {
  return (
    await request<{ mapping: LoadImportMapping | null }>(
      `/api/v1/companies/${companyId}/load-import-mapping?fingerprint=${encodeURIComponent(fingerprint)}`,
    )
  ).mapping;
}
export async function saveLoadImportMapping(
  companyId: string,
  input: {
    name: string;
    fingerprint: string;
    headerRow: number;
    dataStartRow: number;
    columns: Record<string, number>;
  },
): Promise<LoadImportMapping> {
  return (
    await request<{ mapping: LoadImportMapping }>(
      `/api/v1/companies/${companyId}/load-import-mapping`,
      { method: "PUT", body: JSON.stringify(input) },
    )
  ).mapping;
}
export async function updateLoad(
  loadId: string,
  input: CreateLoadInput,
): Promise<"material" | "minor"> {
  return (
    await request<{ amendmentKind: "material" | "minor" }>(
      `/api/v1/loads/${loadId}`,
      { method: "PATCH", body: JSON.stringify(input) },
    )
  ).amendmentKind;
}
export async function closeLoad(
  loadId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/loads/${loadId}`, {
    method: "DELETE",
    body: JSON.stringify({ companyId }),
  });
}
export async function getLoadReport(
  companyId: string,
  filters: LoadReportFilters,
): Promise<LoadReport> {
  const q = new URLSearchParams({ companyId });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) q.set(key, value);
  });
  return request(`/api/v1/reports/loads?${q}`);
}
export function downloadLoadReport(
  companyId: string,
  filters: LoadReportFilters,
  format: "xlsx" | "pdf",
) {
  const q = new URLSearchParams({ companyId });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) q.set(key, value);
  });
  window.location.assign(`${apiBaseUrl}/api/v1/reports/loads.${format}?${q}`);
}
export async function respondLoadAvailability(
  loadId: string,
  input: {
    companyId: string;
    response: "available" | "unavailable";
    note?: string;
    proposedCollectionEta?: string;
    proposedDeliveryEta?: string;
  },
) {
  await request(`/api/v1/loads/${loadId}/availability`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}
export async function placeBid(
  loadId: string,
  input: BidInput,
): Promise<string> {
  return (
    await request<{ id: string }>(`/api/v1/loads/${loadId}/bid`, {
      method: "PUT",
      body: JSON.stringify(input),
    })
  ).id;
}
export async function withdrawBid(
  loadId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/bid?companyId=${companyId}`, {
    method: "DELETE",
  });
}
export async function acceptBid(
  loadId: string,
  bidId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/bids/${bidId}/accept`, {
    method: "POST",
    body: JSON.stringify({ companyId }),
  });
}
export async function markLoadMessagesRead(
  loadId: string,
  companyId: string,
  carrierCompanyId: string,
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/messages/read`, {
    method: "PUT",
    body: JSON.stringify({ companyId, carrierCompanyId }),
  });
}
export async function listConversations(
  companyId: string,
): Promise<LoadConversationSummary[]> {
  return (
    await request<{ conversations: LoadConversationSummary[] }>(
      `/api/v1/messages?companyId=${encodeURIComponent(companyId)}`,
    )
  ).conversations;
}
export async function listLoadMessages(
  loadId: string,
  companyId: string,
  carrierCompanyId?: string,
): Promise<LoadMessage[]> {
  const query = new URLSearchParams({ companyId });
  if (carrierCompanyId) query.set("carrierCompanyId", carrierCompanyId);
  return (
    await request<{ messages: LoadMessage[] }>(
      `/api/v1/loads/${loadId}/messages?${query}`,
    )
  ).messages;
}
export async function addLoadStopDetails(
  loadId: string,
  companyId: string,
  stops: Array<{
    order: number;
    siteName?: string;
    address?: string;
    contactName?: string;
    contactTelephone?: string;
    contactEmail?: string;
    accessInstructions?: string;
    openingHours?: string;
    instructions?: string;
  }>,
): Promise<void> {
  await request(`/api/v1/loads/${loadId}/stops/details`, {
    method: "PATCH",
    body: JSON.stringify({ companyId, stops }),
  });
}
export async function sendLoadMessage(
  loadId: string,
  companyId: string,
  body: string,
  carrierCompanyId?: string,
): Promise<LoadMessage> {
  return (
    await request<{ message: LoadMessage }>(
      `/api/v1/loads/${loadId}/messages`,
      {
        method: "POST",
        body: JSON.stringify({
          companyId,
          body,
          ...(carrierCompanyId ? { carrierCompanyId } : {}),
        }),
      },
    )
  ).message;
}

export async function getPod(
  loadId: string,
  companyId: string,
): Promise<{ pod: LoadPod | null }> {
  return request<{ pod: LoadPod | null }>(
    `/api/v1/loads/${loadId}/pod?companyId=${companyId}`,
  );
}
export async function submitPod(
  loadId: string,
  input: {
    companyId: string;
    recipientName: string;
    signatureData?: string;
    notes?: string;
  },
): Promise<LoadPod> {
  try {
    return (
      await request<{ pod: LoadPod }>(`/api/v1/loads/${loadId}/pod`, {
        method: "PUT",
        body: JSON.stringify(input),
      })
    ).pod;
  } catch (error) {
    if (!offlineEligible(error)) throw error;
    await queueJson(`${apiBaseUrl}/api/v1/loads/${loadId}/pod`, "PUT", input);
    return null as unknown as LoadPod;
  }
}
export async function uploadPodEvidence(
  loadId: string,
  companyId: string,
  kind: "paperwork" | "photo",
  file: File,
): Promise<PodEvidence> {
  const path = `/api/v1/loads/${loadId}/pod/evidence?companyId=${companyId}&kind=${kind}`,
    body = new FormData();
  body.append("file", file);
  try {
    return (
      await request<{ evidence: PodEvidence }>(path, { method: "POST", body })
    ).evidence;
  } catch (error) {
    if (!offlineEligible(error)) throw error;
    await queueFile(`${apiBaseUrl}${path}`, file);
    return null as unknown as PodEvidence;
  }
}
export async function reviewPod(
  loadId: string,
  companyId: string,
  action: "approve" | "changes_requested",
  reason?: string,
): Promise<LoadPod> {
  return (
    await request<{ pod: LoadPod }>(`/api/v1/loads/${loadId}/pod/review`, {
      method: "POST",
      body: JSON.stringify({
        companyId,
        action,
        ...(reason ? { reason } : {}),
      }),
    })
  ).pod;
}
export async function sendPod(
  loadId: string,
  companyId: string,
): Promise<LoadPod> {
  return (
    await request<{ pod: LoadPod }>(`/api/v1/loads/${loadId}/pod/send`, {
      method: "POST",
      body: JSON.stringify({ companyId }),
    })
  ).pod;
}
export async function downloadPodEvidence(
  loadId: string,
  companyId: string,
  evidenceId: string,
): Promise<void> {
  const result = await request<{ url: string }>(
    `/api/v1/loads/${loadId}/pod/evidence/${evidenceId}?companyId=${companyId}`,
  );
  window.location.assign(result.url);
}

export async function getLoadCase(
  loadId: string,
  companyId: string,
): Promise<CaseWorkflow> {
  return request<CaseWorkflow>(
    `/api/v1/loads/${loadId}/case?companyId=${companyId}`,
  );
}
export async function requestExit(
  loadId: string,
  input: {
    companyId: string;
    kind: "poster_cancellation" | "carrier_withdrawal";
    reason: string;
    proposedChargePence?: number;
  },
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/exit-requests`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function respondExit(
  loadId: string,
  id: string,
  input: {
    companyId: string;
    action: "accept" | "reject" | "counter";
    counterChargePence?: number;
    note?: string;
  },
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/exit-requests/${id}/respond`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function finaliseExit(
  loadId: string,
  id: string,
  input: { companyId: string; action: "close" | "reopen" },
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/exit-requests/${id}/finalise`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function escalateExit(
  loadId: string,
  id: string,
  companyId: string,
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/exit-requests/${id}/escalate`, {
    method: "POST",
    body: JSON.stringify({ companyId }),
  });
}
export async function raiseDispute(
  loadId: string,
  input: {
    companyId: string;
    category: DisputeCategory;
    summary: string;
    details: string;
  },
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/disputes`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function uploadDisputeEvidence(
  loadId: string,
  id: string,
  companyId: string,
  file: File,
): Promise<CaseWorkflow> {
  const body = new FormData();
  body.append("file", file);
  return request(
    `/api/v1/loads/${loadId}/disputes/${id}/evidence?companyId=${companyId}`,
    { method: "POST", body },
  );
}
export async function downloadDisputeEvidence(
  loadId: string,
  id: string,
  evidenceId: string,
  companyId: string,
) {
  const r = await request<{ url: string }>(
    `/api/v1/loads/${loadId}/disputes/${id}/evidence/${evidenceId}?companyId=${companyId}`,
  );
  window.location.assign(r.url);
}
export async function resolveDispute(
  loadId: string,
  id: string,
  input: { companyId: string; outcome: string; close: boolean },
): Promise<CaseWorkflow> {
  return request(`/api/v1/loads/${loadId}/disputes/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getInvoice(
  loadId: string,
  companyId: string,
): Promise<InvoiceWorkflow> {
  return request<InvoiceWorkflow>(
    `/api/v1/loads/${loadId}/invoice?companyId=${companyId}`,
  );
}
export async function includeDisputeMessage(
  loadId: string,
  id: string,
  messageId: string,
  companyId: string,
): Promise<CaseWorkflow> {
  return request(
    `/api/v1/loads/${loadId}/disputes/${id}/messages/${messageId}/include`,
    { method: "POST", body: JSON.stringify({ companyId }) },
  );
}
export async function proposeCharge(
  loadId: string,
  input: {
    companyId: string;
    type: "waiting" | "demurrage" | "other";
    description: string;
    amountPence: number;
  },
): Promise<AdditionalCharge> {
  return (
    await request<{ charge: AdditionalCharge }>(
      `/api/v1/loads/${loadId}/charges`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).charge;
}
export async function reviewCharge(
  loadId: string,
  chargeId: string,
  input: { companyId: string; approved: boolean; reason?: string },
): Promise<AdditionalCharge> {
  return (
    await request<{ charge: AdditionalCharge }>(
      `/api/v1/loads/${loadId}/charges/${chargeId}/review`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).charge;
}
export async function createInvoice(
  loadId: string,
  input: {
    companyId: string;
    invoiceNumber?: string;
    vatRateBps: number;
    paymentTermsDays: number;
    bankDetails: Record<string, string>;
    notes?: string;
  },
): Promise<LoadInvoice> {
  return (
    await request<{ invoice: LoadInvoice }>(`/api/v1/loads/${loadId}/invoice`, {
      method: "PUT",
      body: JSON.stringify(input),
    })
  ).invoice;
}
export async function sendInvoice(
  loadId: string,
  companyId: string,
): Promise<LoadInvoice> {
  return (
    await request<{ invoice: LoadInvoice }>(
      `/api/v1/loads/${loadId}/invoice/send`,
      { method: "POST", body: JSON.stringify({ companyId }) },
    )
  ).invoice;
}
export async function setInvoiceStatus(
  loadId: string,
  input: {
    companyId: string;
    status: "paid" | "overdue" | "disputed";
    reason?: string;
  },
): Promise<LoadInvoice> {
  return (
    await request<{ invoice: LoadInvoice }>(
      `/api/v1/loads/${loadId}/invoice/status`,
      { method: "POST", body: JSON.stringify(input) },
    )
  ).invoice;
}

export async function getBillingSummary(
  companyId: string,
): Promise<BillingSummary> {
  return (
    await request<{ billing: BillingSummary }>(
      `/api/v1/companies/${companyId}/billing`,
    )
  ).billing;
}
export async function createBillingCheckout(
  companyId: string,
  selection: CheckoutSelection,
): Promise<{ url: string }> {
  return request(`/api/v1/companies/${companyId}/billing/checkout`, {
    method: "POST",
    body: JSON.stringify(selection),
  });
}
export async function createBillingPortal(
  companyId: string,
): Promise<{ url: string }> {
  return request(`/api/v1/companies/${companyId}/billing/portal`, {
    method: "POST",
  });
}
export async function requestMoreManagedSeats(
  companyId: string,
  groupId: string,
): Promise<{ id: string }> {
  return (
    await request<{ request: { id: string } }>(
      `/api/v1/companies/${companyId}/billing/seat-requests`,
      { method: "POST", body: JSON.stringify({ groupId }) },
    )
  ).request;
}
export async function decideBillingSeatRequest(
  companyId: string,
  requestId: string,
  approve: boolean,
): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/billing/seat-requests/${requestId}/decision`,
    { method: "POST", body: JSON.stringify({ approve }) },
  );
}
export async function getBillingVouchers(): Promise<{
  environment: "live" | "sandbox";
  products: BillingVoucherProduct[];
  vouchers: BillingVoucher[];
}> {
  return request("/api/v1/admin/billing/vouchers");
}
export async function createBillingVoucher(
  input: VoucherInput,
): Promise<{ voucher: BillingVoucher }> {
  return request("/api/v1/admin/billing/vouchers", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export async function setBillingVoucherActive(
  id: string,
  active: boolean,
): Promise<void> {
  await request(`/api/v1/admin/billing/vouchers/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}
export async function setPublicBillingVoucher(
  voucherId: string | null,
  label: string,
): Promise<void> {
  await request("/api/v1/admin/billing/public-voucher", {
    method: "PUT",
    body: JSON.stringify({ voucherId, label }),
  });
}

export async function getNotifications(): Promise<NotificationFeed> {
  return request<NotificationFeed>("/api/v1/notifications");
}
export async function markNotificationRead(id: string): Promise<void> {
  await request(`/api/v1/notifications/${id}/read`, { method: "PUT" });
}
export async function markAllNotificationsRead(): Promise<void> {
  await request("/api/v1/notifications/read-all", { method: "PUT" });
}

export type InsuranceRule = {
  insuranceType: string;
  label: string;
  description: string;
  level: "not_required" | "advisory" | "mandatory";
  minimumCoverGbp: number | null;
  evidenceRequired: boolean;
  warningDays: number;
  graceDays: number;
  materialVersion: number;
  acknowledgedVersion: number | null;
  requiresAcknowledgement: boolean;
};
export type GroupInsuranceRules = {
  groupId: string;
  groupName: string;
  isOwner: boolean;
  isAdmin: boolean;
  membershipStatus: string | null;
  rules: InsuranceRule[];
};
export type InsuranceFinding = {
  groupId: string;
  groupName: string;
  insuranceType: string;
  label: string;
  level: "advisory" | "mandatory";
  status: string;
  message: string;
  blocksBid: boolean;
};
export type InsuranceCheck = {
  allowed: boolean;
  findings: InsuranceFinding[];
  checkedAt: string;
};
export async function getGroupInsurance(
  groupId: string,
  companyId: string,
): Promise<GroupInsuranceRules> {
  return request(`/api/v1/groups/${groupId}/insurance?companyId=${companyId}`);
}
export async function saveGroupInsurance(
  groupId: string,
  companyId: string,
  rules: InsuranceRule[],
): Promise<GroupInsuranceRules> {
  return request(`/api/v1/groups/${groupId}/insurance`, {
    method: "PUT",
    body: JSON.stringify({ companyId, rules }),
  });
}
export async function acknowledgeGroupInsurance(
  groupId: string,
  companyId: string,
): Promise<GroupInsuranceRules> {
  return request(`/api/v1/groups/${groupId}/insurance/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ companyId }),
  });
}
export async function getLoadInsuranceCheck(
  loadId: string,
  companyId: string,
): Promise<InsuranceCheck> {
  return request(
    `/api/v1/loads/${loadId}/insurance-check?companyId=${companyId}`,
  );
}

async function request<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    cache: "no-store",
    credentials: "include",
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...(init.method &&
      !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase())
        ? { "x-idempotency-key": crypto.randomUUID() }
        : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
      message?: string;
    } | null;
    throw new ApiError(
      payload?.error?.message ?? payload?.message ?? "Something went wrong",
      response.status,
      payload?.error?.code,
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export type CompanyNotificationDefaults = {
  emailEnabled: boolean;
  pushEnabled: boolean;
};
export async function getPushConfiguration(): Promise<{
  enabled: boolean;
  publicKey: string | null;
}> {
  return request("/api/v1/notifications/push/configuration");
}
export async function savePushSubscription(
  subscription: PushSubscription,
): Promise<void> {
  const value = subscription.toJSON();
  await request("/api/v1/notifications/push/subscription", {
    method: "PUT",
    body: JSON.stringify(value),
  });
}
export async function removePushSubscription(endpoint: string): Promise<void> {
  await request("/api/v1/notifications/push/subscription", {
    method: "DELETE",
    body: JSON.stringify({ endpoint }),
  });
}
export async function getCompanyNotificationDefaults(
  companyId: string,
): Promise<CompanyNotificationDefaults> {
  return (
    await request<{ defaults: CompanyNotificationDefaults }>(
      `/api/v1/companies/${companyId}/notification-defaults`,
    )
  ).defaults;
}
export async function setCompanyNotificationDefaults(
  companyId: string,
  defaults: CompanyNotificationDefaults,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/notification-defaults`, {
    method: "PUT",
    body: JSON.stringify(defaults),
  });
}

export type NotificationDeliveryJob = {
  id: string;
  notificationId: string;
  userId: string;
  companyId: string;
  channel: "email" | "web_push";
  status: string;
  attempts: number;
  availableAt: string;
  lastError: string | null;
  providerMessageId: string | null;
  createdAt: string;
  sentAt: string | null;
};
export async function getFailedNotificationDeliveries(): Promise<
  NotificationDeliveryJob[]
> {
  return (
    await request<{ deliveries: NotificationDeliveryJob[] }>(
      "/api/v1/admin/notification-deliveries/failed",
    )
  ).deliveries;
}
export async function getAdminProspects(): Promise<AdminProspect[]> {
  return (
    await request<{ prospects: AdminProspect[] }>("/api/v1/admin/prospects")
  ).prospects;
}
export async function setAdminProspectContacted(
  id: string,
  contacted: boolean,
): Promise<void> {
  await request(`/api/v1/admin/prospects/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ contacted }),
  });
}
export async function sendAdminProspectEmail(id: string): Promise<void> {
  await request(`/api/v1/admin/prospects/${id}/send`, { method: "POST" });
}
export async function sendAdminProspectEmails(ids: string[]): Promise<{
  results: Array<{ id: string; status: "sent" | "failed"; error?: string }>;
}> {
  return request("/api/v1/admin/prospects/send", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}
export async function getAdminCampaigns(): Promise<AdminCampaign[]> {
  return (
    await request<{ campaigns: AdminCampaign[] }>("/api/v1/admin/campaigns")
  ).campaigns;
}
export async function saveAdminCampaign(input: AdminCampaignInput, key?: string): Promise<{key:string}> {
  return request(key ? `/api/v1/admin/campaigns/${key}` : "/api/v1/admin/campaigns", {
    method: key ? "PUT" : "POST",
    body: JSON.stringify(input),
  });
}
export async function getAdminCampaignCompanies(
  key: AdminCampaign["key"],
): Promise<AdminCampaignCompany[]> {
  return (
    await request<{ companies: AdminCampaignCompany[] }>(
      `/api/v1/admin/campaigns/${key}/companies`,
    )
  ).companies;
}
export async function sendAdminCampaign(
  key: AdminCampaign["key"],
  companyIds: string[],
): Promise<{ results: Array<{ companyId: string; status: string }> }> {
  return request(`/api/v1/admin/campaigns/${key}/send`, {
    method: "POST",
    body: JSON.stringify({ companyIds }),
  });
}
export async function getAdminCampaignFeedback(): Promise<
  AdminCampaignFeedback[]
> {
  return (
    await request<{ feedback: AdminCampaignFeedback[] }>(
      "/api/v1/admin/campaign-feedback",
    )
  ).feedback;
}
export async function awardLaunchPack(feedbackId: string): Promise<void> {
  await request(`/api/v1/admin/campaign-feedback/${feedbackId}/launch-pack`, {
    method: "POST",
  });
}
export async function submitCampaignFeedback(
  token: string,
  feedback: string,
): Promise<void> {
  await request("/api/v1/public/campaign-feedback", {
    method: "POST",
    body: JSON.stringify({ token, feedback }),
  });
}
export async function claimLaunchPack(
  token: string,
  companyId: string,
  groupId: string,
): Promise<void> {
  await request("/api/v1/launch-packs/claim", {
    method: "POST",
    body: JSON.stringify({ token, companyId, groupId }),
  });
}
export async function grantLaunchPackMembership(
  groupId: string,
  companyId: string,
): Promise<void> {
  await request(`/api/v1/groups/${groupId}/launch-pack-memberships`, {
    method: "POST",
    body: JSON.stringify({ companyId }),
  });
}

function offlineEligible(error: unknown) {
  return !navigator.onLine || error instanceof TypeError;
}

export type ReputationReview = {
  id: string;
  loadId: string | null;
  reviewerName: string;
  sentiment: "positive" | "neutral" | "negative";
  text: string;
  status: "published" | "under_review";
  createdAt: string;
  updatedAt: string;
  reply: { text: string; createdAt: string } | null;
  media: Array<{ id: string; filename: string }>;
};
export type ReputationSummary = {
  companyId: string;
  foundingEarlyAdopter: boolean;
  positive: number;
  neutral: number;
  negative: number;
  scoredReviews: number;
  positivePercentage: number | null;
  reviews: ReputationReview[];
  references: Array<{
    id: string;
    authorName: string;
    relationship: string;
    text: string;
    createdAt: string;
  }>;
  externalSources: Array<{
    id: string;
    provider: string;
    profileUrl: string;
    createdAt: string;
    media: Array<{ id: string; filename: string }>;
  }>;
};
export type ReviewOpportunity = {
  loadId: string;
  reference: string;
  status: string;
  reviewedCompanyId: string;
  reviewedCompanyName: string;
  reviewed: boolean;
};

export type PublicNetworkTier = "load_provider" | "full_network";
export type PublicNetworkInterest = {
  companyId: string;
  tier: PublicNetworkTier;
  status:
    | "interested"
    | "withdrawn"
    | "invited"
    | "pending_payment"
    | "active"
    | "cancelling"
    | "cancelled"
    | "payment_failed";
  earlyAdopterEligible: boolean;
  registeredAt: string;
  updatedAt: string;
  activatedAt: string | null;
  rewardedAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  amountPaidPence: number;
  billingInterval: "month" | "year";
  foundingEarlyAdopter: boolean;
};
export async function getPublicNetworkInterest(
  companyId: string,
): Promise<PublicNetworkInterest | null> {
  return (
    await request<{ interest: PublicNetworkInterest | null }>(
      `/api/v1/companies/${companyId}/public-network-interest`,
    )
  ).interest;
}
export async function registerPublicNetworkInterest(
  companyId: string,
  tier: PublicNetworkTier,
): Promise<PublicNetworkInterest> {
  return (
    await request<{ interest: PublicNetworkInterest }>(
      `/api/v1/companies/${companyId}/public-network-interest`,
      { method: "PUT", body: JSON.stringify({ tier }) },
    )
  ).interest;
}
export async function withdrawPublicNetworkInterest(
  companyId: string,
): Promise<void> {
  await request(`/api/v1/companies/${companyId}/public-network-interest`, {
    method: "DELETE",
  });
}
export async function createPublicNetworkCheckout(
  companyId: string,
  tier: PublicNetworkTier,
  interval: "month" | "year" = "month",
): Promise<{ url: string }> {
  return request(
    `/api/v1/companies/${companyId}/billing/public-network/checkout`,
    { method: "POST", body: JSON.stringify({ tier, interval }) },
  );
}
export async function cancelPublicNetwork(companyId: string): Promise<void> {
  await request(
    `/api/v1/companies/${companyId}/billing/public-network/cancel`,
    { method: "POST" },
  );
}

export type SeoRangeDays = 7 | 28 | 90;
export type SeoPerformance = {
  configured: boolean;
  property: string | null;
  rangeDays: SeoRangeDays;
  startDate: string | null;
  endDate: string | null;
  comparedStartDate: string | null;
  updatedAt: string;
  totals: { clicks: number; impressions: number; ctr: number; position: number | null };
  previousTotals: { clicks: number; impressions: number; ctr: number; position: number | null };
  rows: Array<{
    query: string;
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
    previousPosition: number | null;
    positionChange: number | null;
    clickChange: number | null;
    impressionChange: number | null;
  }>;
};
export async function getAdminSeoPerformance(range: SeoRangeDays): Promise<SeoPerformance> {
  return (await request<{ performance: SeoPerformance }>(`/api/v1/admin/seo?range=${range}`)).performance;
}


export type MemberReferralProgram={
  link:string; attributed:number; qualified:number; reversed:number; trialling:number; notConverted:number; nextRewardAt:number; progress:number;
  source:{code:string;memberCommissionQualifiedAt:string|null};
  commission:{grossPaidPence:number;commissionPence:number};
  rewards:Array<{id:string;sequence_number:number;status:"pending"|"applied"|"reversed";amount_pence:number|null;currency:string;earned_at:string;applied_at:string|null;reversed_at:string|null}>;
  referrals:Array<{referred_company_id:string;trading_name:string;status:"trialling"|"not_converted"|"qualified"|"reversed";joined_at:string;qualified_at:string|null;reversed_at:string|null;reversal_reason:string|null}>;
};
export function getMemberReferralProgram(companyId:string){return request<MemberReferralProgram>(`/api/v1/companies/${companyId}/referral-program`);}
